import cn from 'classnames';
import Link from 'next/link';
// Types
import {
  FeaturedStreamsType,
  getFeaturedStreamActionState,
  getFeaturedSlideDetails,
} from '../FeaturedStreamsCarousel.helpers';
// UI
import Text from 'ui3/Text/Text';
// Components
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
import TalentBlock from 'components/common3/TalentBlock/TalentBlock';
import GetTicketsButton from 'components/TicketEvents/GetTicketsButton/GetTicketsButton';
// Helpers
import { parseToPlainText } from 'helpers/textEditor';
import {
  getStreamSocialShareText,
  getStreamerAvatar,
  getStreamerName,
} from 'helpers/streams';
// Hooks
import { useGetCurrUser, useStreamPurchaseNavigation } from 'hooks';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Styles
import styles from './DesktopCarousel.module.scss';

interface DesktopCarouselProps {
  streams: FeaturedStreamsType[];
  currentSlide: number;
  onSlideChange: (slideIndex: number) => void;
  fullWidth?: boolean;
  withStoreChannelLink?: string;
  useArrowNavigation?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  isProfilePage?: boolean;
  showOfficialPpvTag?: boolean;
  withOrganizer?: boolean;
  /** Pauses autoplay while a checkout opened from a slide is open. */
  onCheckoutOpenChange?: (open: boolean) => void;
}

const DesktopCarousel = ({
  streams,
  currentSlide,
  fullWidth = false,
  withStoreChannelLink,
  onPrev,
  onNext,
  showOfficialPpvTag = true,
  withOrganizer = false,
  onCheckoutOpenChange,
}: DesktopCarouselProps) => {
  const { data: userData } = useGetCurrUser();
  const { navigateToStream } = useStreamPurchaseNavigation();
  const ticketingLaunched = useTicketingLaunched();

  const currentStream = streams?.[currentSlide];

  if (!currentStream || !currentStream.stream) {
    return null;
  }

  const stream = currentStream.stream;
  const {
    buttonText,
    hasAccessToWatch,
    isUserStreamCreator,
    showOfficialPpvTagForSlide,
  } = getFeaturedStreamActionState(
    stream,
    userData?.me?.id,
    showOfficialPpvTag
  );

  const handleBuyButtonClick = () => {
    navigateToStream(currentStream.stream, hasAccessToWatch);
  };

  const hasMultipleStreams = streams.length > 1;
  const {
    streamPath,
    dateLabel,
    organizer,
    ticketEvent,
  } = getFeaturedSlideDetails(stream, withOrganizer);

  // Parse description for ellipsis
  const descriptionPlainText = parseToPlainText(
    currentStream.stream.description
  );

  // Social share text
  const streamerName = getStreamerName(currentStream.stream.store);
  const socialShareButtonText = getStreamSocialShareText(
    isUserStreamCreator,
    false,
    streamerName
  );

  return (
    <div
      className={cn(styles.desktopCarousel, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      {withStoreChannelLink && (
        <div className={styles.buttonContainer}>
          <LinkButton
            link={withStoreChannelLink}
            variant="secondary"
            subVariant="text"
            iconSize={16}
            icon="chevronRight"
            iconPosition="right"
          >
            See All
          </LinkButton>
        </div>
      )}
      {/* Left Panel - Event Details */}
      <div className={styles.leftPanel}>
        <div className={styles.eventDetails}>
          <div className={styles.tagsRow}>
            {showOfficialPpvTagForSlide && (
              <div className={styles.officialPpvTag}>
                <Text variant="smallLabelBold" color="background-dim">
                  Official PPV
                </Text>
              </div>
            )}
            <div className={styles.dateTag}>
              <Text variant="smallLabelBold" color="background-dim">
                {dateLabel}
              </Text>
            </div>
          </div>

          <Link href={streamPath} prefetch={false}>
            <a className={styles.titleLink}>
              <Text variant="h4" className={styles.title}>
                {currentStream.stream.name}
              </Text>
            </a>
          </Link>

          {organizer && (
            <TalentBlock
              store={organizer}
              talentName={streamerName}
              avatarURL={getStreamerAvatar(organizer.storeDetails)}
              profilePath={`/${organizer.slug}`}
              showFollowButton={!isUserStreamCreator}
            />
          )}

          {descriptionPlainText && (
            <div className={styles.descriptionBlock}>
              <div
                className={cn(styles.description, {
                  [styles.descriptionCompact]: withOrganizer,
                })}
              >
                {descriptionPlainText}
              </div>
              {withOrganizer && (
                <Link href={streamPath} prefetch={false}>
                  <a className={styles.readMore}>Read More</a>
                </Link>
              )}
            </div>
          )}
        </div>

        <div className={styles.actionButtons}>
          <Button
            variant="primary"
            size="large"
            className={styles.buyButton}
            onClick={handleBuyButtonClick}
          >
            {buttonText}
          </Button>
          {ticketingLaunched && ticketEvent && (
            <GetTicketsButton
              key={ticketEvent.id}
              event={ticketEvent}
              size="large"
              onOpenChange={onCheckoutOpenChange}
            />
          )}
          {!withOrganizer && (
            <SocialShareQRButton
              onlyIcon
              itemTitle={currentStream.stream.name || ''}
              modalTitle="Share Stream"
              modalText="Share this stream in any way convenient for you"
              path={streamPath}
              twitterText={socialShareButtonText}
            />
          )}
        </div>
      </div>

      {/* Arrow Navigation */}
      {hasMultipleStreams && (
        <>
          <button
            type="button"
            className={styles.arrowButton}
            onClick={onPrev}
            aria-label="Previous"
          >
            <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
          </button>
          <button
            type="button"
            className={styles.arrowButton}
            onClick={onNext}
            aria-label="Next"
          >
            <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
          </button>
        </>
      )}

      {/* Right Panel - Single Image */}
      <div className={styles.rightPanel}>
        <div className={styles.imageContainer}>
          {currentStream.stream.imageURL && (
            <img
              src={currentStream.stream.imageURL}
              alt={currentStream.stream.name || 'Event poster'}
              className={styles.posterImage}
            />
          )}
          <div className={styles.imageShading} />
        </div>
      </div>
    </div>
  );
};

export default DesktopCarousel;
